import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
  Step,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const ProposalsAndQuotes = () => (
  <Page
    title="Proposals and Quotes"
    intro="Sales tracking sits alongside event planning: prospective events move across the proposals board while you build and send quotes, and once won they carry on into full planning on the same record."
  >
    <Section title="The Sales and Proposals board">
      <Bullets>
        <Bullet>
          Open <DocLink url="/events/proposals" text="Proposals" /> from the
          sidebar under <strong>Events</strong>. The board has three columns —{' '}
          <strong>Potential</strong>, <strong>Interested</strong>, and{' '}
          <strong>Quote Sent</strong> — showing upcoming events in those
          statuses, with the client name and start date on each card.
        </Bullet>
        <Bullet>
          Cards aren't dragged between columns: click a card to open the
          event, then change its status from the status chip at the top of the
          event record. Once the status moves on to Planning (or beyond) the
          event leaves the board.
        </Bullet>
        <Bullet>
          Only future-dated events appear here — a proposal whose start date
          has passed drops off the board.
        </Bullet>
        <Bullet>
          The <strong>New Quote</strong> and <strong>New Client</strong>{' '}
          buttons at the top jump straight into building a quote or creating a
          client record.
        </Bullet>
      </Bullets>
      <Info title="Who can do this?">
        Proposals and quotes need the <strong>Event Planning</strong> team on
        your staff profile (site administrators always have access).
      </Info>
    </Section>

    <Section title="Building a quote">
      <Step number="1" title="Open the quote builder">
        Either use <strong>Create quote</strong> from the plus menu on the
        event record's <strong>Invoicing</strong> panel (the client and event
        are pre-selected), or start from{' '}
        <DocLink url="/clients/newquote" text="New Quote" /> and pick the
        client and event — both can be created inline if they don't exist
        yet.
      </Step>
      <Step number="2" title="Add personnel">
        Use <strong>Add Shift</strong> in the Personnel panel to add each
        staffing line: choose the <strong>Grade</strong>, then either a fixed
        rate or hours at that grade's default hourly rate. If no hourly rate
        is configured for a grade, you'll be prompted to set one in Site
        Configuration or switch to a fixed rate.
      </Step>
      <Step number="3" title="Add the other charges">
        Fill in <strong>Number of Vehicles</strong>,{' '}
        <strong>Number of Miles</strong>, <strong>Accommodation</strong>,{' '}
        <strong>Treatment Centre Builds</strong>, and the{' '}
        <strong>Admin Charge</strong>. The switches control whether mileage
        and accommodation are charged to the customer or absorbed as a
        business cost.
      </Step>
      <Step number="4" title="Review the summary and submit">
        The Quote Summary shows the customer-facing lines, your business
        costs, and a colour-coded <strong>Profit Margin</strong> slider, plus
        an optional <strong>Discount</strong>, the{' '}
        <strong>Amount Due</strong>, and <strong>Total Profit</strong>.
        Defaults for the admin charge, treatment centre build, profit margin,
        cost per mile, and make-ready cost come from your quotes and invoices
        settings in Site Configuration. Submitting saves the quote against
        the event and returns you to its record.
      </Step>
      <Warning title="Draft quotes edit in place; anything sent is versioned">
        Opening a <strong>Draft</strong> quote with the edit (pencil) icon
        updates that same quote in place. Once a quote is Sent, Accepted, or
        Declined it is a record of what was offered: editing it saves a{' '}
        <strong>new</strong> quote alongside the original, and a{' '}
        <strong>Summary of Changes</strong> is required — the server refuses
        in-place edits of anything past Draft. The earlier quote stays on the
        event, so the history of what was offered is preserved.
      </Warning>
    </Section>

    <Section title="Sending, accepting, and declining">
      <Bullets>
        <Bullet>
          A quote starts as <strong>Draft</strong>. <strong>Send</strong>{' '}
          emails a summary (total and validity date) to the client's finance
          contact — or the organisation email if there is no finance contact —
          marks the quote <strong>Sent</strong>, and stamps it valid for 30
          days. <strong>Resend</strong> is available afterwards.
        </Bullet>
        <Bullet>
          <strong>Accept</strong> records the acceptance date, and{' '}
          <strong>Decline</strong> records the date with an optional reason.
          Both are only possible while the quote is Draft or Sent.
        </Bullet>
        <Bullet>
          Quotes are listed on the event's <strong>Invoicing</strong> panel
          with their value, date, author, and status, alongside the event's
          invoices.
        </Bullet>
      </Bullets>
      <Info title="From quote to invoice">
        Accepting a quote does not raise an invoice automatically. Use{' '}
        <strong>Invoice from accepted quote</strong> in the plus menu of the
        event's Invoicing panel to pre-fill an invoice from the accepted
        quote — the option is disabled until a quote has been accepted.
        Invoicing itself — references, VAT, payment schedules, and credit
        notes — is covered in the Finance documentation section.
      </Info>
    </Section>
  </Page>
)

export default ProposalsAndQuotes
